/**
 * Onglet Vitrine — édition + publication de la boutique.
 *
 * Rôle : permettre à la patronne de publier sa boutique sur internet
 * (page /v/:id) en 1 geste. Aucune compétence technique.
 *
 * États :
 * - non publiée : formulaire + bouton "Publier"
 * - publiée : lien partageable + QR code + bouton "Modifier" / "Dépublier"
 */
import { useEffect, useState } from 'react';
import { tokens, session, rafraichirSession } from '../lib/api';
import { vitrineApi, type VitrineMe } from '../lib/vitrine';

const CORE_URL = (import.meta.env.VITE_CORE_URL as string | undefined) ?? '';
// URL de la vitrine publique (même origine que l'App PWA, côté front).
// En prod ce sera /v/:id routé par le worker Vitrine, mais en attendant
// l'endpoint public GET /vitrine/public/:id est consommable directement.
const PUBLIC_VITRINE_BASE = (import.meta.env.VITE_PUBLIC_VITRINE_URL as string | undefined) ?? CORE_URL;

export function Vitrine() {
  const [etat, setEtat] = useState<VitrineMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [sauvegarde, setSauvegarde] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [boutiqueId, setBoutiqueId] = useState<string | null>(null);

  const [slogan, setSlogan] = useState('');
  const [horaires, setHoraires] = useState('');
  const [description, setDescription] = useState('');

  useEffect(() => {
    void (async () => {
      const t = tokens.get();
      if (!t) {
        setMessage('⚠️ Connecte-toi d\'abord (Réglages) pour gérer ta vitrine.');
        setLoading(false);
        return;
      }
      // Récupère la session (donne le boutiqueId) et l'état vitrine
      const s = await rafraichirSession();
      if (s) setBoutiqueId(s.boutiqueId);
      try {
        const data = await vitrineApi.me();
        setEtat(data);
        setSlogan(data.slogan ?? '');
        setHoraires(data.horaires ?? '');
        setDescription(data.description ?? '');
      } catch (e) {
        setMessage(`Erreur : ${(e as Error).message}`);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const publier = async () => {
    setSauvegarde(true);
    setMessage(null);
    try {
      const r = await vitrineApi.publier({
        active: true,
        slogan: slogan || undefined,
        horaires: horaires || undefined,
        description: description || undefined,
      });
      setEtat({ active: r.active, slogan, horaires, description });
      setMessage('✅ Vitrine publiée ! Partage le lien ci-dessous.');
    } catch (e) {
      setMessage(`❌ ${(e as Error).message}`);
    } finally {
      setSauvegarde(false);
    }
  };

  const depublicer = async () => {
    setSauvegarde(true);
    try {
      await vitrineApi.publier({ active: false });
      setEtat((e) => (e ? { ...e, active: false } : e));
      setMessage('Vitrine dépubliée.');
    } catch (e) {
      setMessage(`❌ ${(e as Error).message}`);
    } finally {
      setSauvegarde(false);
    }
  };

  const copier = () => {
    const url = `${PUBLIC_VITRINE_BASE}/vitrine/public/${boutiqueId ?? ''}`;
    void navigator.clipboard.writeText(url).then(() => setMessage('✅ Lien copié'));
  };

  if (loading) return <p className="etat-vide">⏳ Chargement…</p>;

  return (
    <>
      <h1 className="titre-section">🌐 Ma vitrine</h1>

      {message && (
        <div className="carte" style={{ background: '#ecfdf5', borderColor: '#a7f3d0' }}>{message}</div>
      )}

      <section className="carte">
        <h3>État</h3>
        <div className="ligne">
          <span>Visibilité</span>
          <span className={`badge ${etat?.active ? 'ouvert' : 'attente'}`}>
            {etat?.active ? '🟢 Publiée' : '⚪ Non publiée'}
          </span>
        </div>
        {etat?.active && boutiqueId && (
          <>
            <div className="ligne" style={{ fontSize: 13, wordBreak: 'break-all' }}>
              <span>Lien</span>
              <a href={`${PUBLIC_VITRINE_BASE}/vitrine/public/${boutiqueId}`} target="_blank" rel="noopener">
                Ouvrir
              </a>
            </div>
            <button className="bouton bouton-secondaire bouton-pleine-largeur" onClick={copier}>
              📋 Copier le lien
            </button>
          </>
        )}
      </section>

      <section className="carte">
        <h3>Informations publiques</h3>
        <Champ
          label="Slogan (max 120 car.)"
          value={slogan}
          onChange={setSlogan}
          maxLength={120}
          placeholder="ex: La qualité à prix juste, depuis 2018"
        />
        <Champ
          label="Horaires (max 120 car.)"
          value={horaires}
          onChange={setHoraires}
          maxLength={120}
          placeholder="ex: Lun-Sam 8h-20h, Dim 9h-13h"
        />
        <label style={{ display: 'block', marginBottom: 10 }}>
          <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--couleur-texte-doux)' }}>
            Description (max 500 car.)
          </div>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={500}
            rows={4}
            placeholder="2-3 phrases qui racontent ta boutique : ce qu'on y trouve, ce qui te distingue…"
            style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--couleur-bordure)', font: 'inherit' }}
          />
        </label>
      </section>

      {etat?.active ? (
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="bouton bouton-primaire"
            style={{ flex: 1 }}
            onClick={publier}
            disabled={sauvegarde}
          >
            {sauvegarde ? '⏳' : '💾'} Mettre à jour
          </button>
          <button
            className="bouton bouton-danger"
            onClick={depublicer}
            disabled={sauvegarde}
          >
            Dépublier
          </button>
        </div>
      ) : (
        <button
          className="bouton bouton-primaire bouton-pleine-largeur"
          onClick={publier}
          disabled={sauvegarde}
        >
          {sauvegarde ? '⏳ Publication…' : '🚀 Publier ma vitrine'}
        </button>
      )}
    </>
  );
}

function Champ(props: { label: string; value: string; onChange: (v: string) => void; maxLength?: number; placeholder?: string }) {
  return (
    <label style={{ display: 'block', marginBottom: 10 }}>
      <div style={{ fontSize: 12, marginBottom: 4, color: 'var(--couleur-texte-doux)' }}>{props.label}</div>
      <input
        type="text"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        maxLength={props.maxLength}
        placeholder={props.placeholder}
        style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--couleur-bordure)' }}
      />
    </label>
  );
}
